<template>
  <div
    class="flex flex-row justify-between items-center"
    style="height: 30.5px;"
  >
    <h1 class="font-semibold leading-none ">
      Api Usage
    </h1>
    <div
      v-if="Object.values(params).length"
      class="flex justify-between items-center"
    >
      <h1 class="text-sm font-medium mr-4">
        Total Last 24h:  <b>{{ Math.abs(apiConsumptionToday) || 0 }}</b>
      </h1>
      <div v-if="Number.isFinite(apiConsumptionDelta)">
        <div
          v-if="apiConsumptionDelta < 0"
          class="flex justify-center items-center h-7 px-2 rounded "
          style="background-color:rgb(121 255 113);box-shadow:0px 6px 20px rgba(139, 252, 97, 0.3);"
        >
          <Message
            icon="pi pi-arrow-down"
            variant="simple"
            severity="success"
          >
            {{ Math.abs(apiConsumptionDelta) || 0 }}%
          </Message>
        </div>
        <div
          v-else
          class="flex justify-center items-center h-7 px-2 rounded "
          style="background-color:#FFB3B3;box-shadow:0px 6px 20px rgba(252, 97, 97, 0.3);"
        >
          <Message
            icon="pi pi-arrow-up"
            variant="simple"
            style="color: #990F0F"
          >
            {{ apiConsumptionDelta || 0 }}%
          </Message>
        </div>
      </div>
    </div>
  </div>

  <div class="flex justify-center w-full">
    <SelectButton
      v-model="value"
      class="flex justify-center w-full"
      :options="options"
      :allow-empty="true"
    />
  </div>
  <Chart
    v-if="Object.values(params).length && _value === 'Hourly'"
    type="line"
    :data="chartDataHourly"
    :options="chartOptionsHourly"
    class="h-[20rem]"
  />
  <Chart
    v-else-if="Object.values(params).length && _value === 'Daily'"
    type="bar"
    :data="chartDataDaily"
    :options="chartOptionsDaily"
    class="h-[20rem]"
  />
  <div
    v-else
    class="flex flex-1 w-full items-center justify-center"
  >
    <ProgressSpinner />
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useDark } from '@vueuse/core'

const params = defineModel({ type: Object, default: { } })
const darkMode = useDark()
const value = ref('Hourly')
const options = ref(['Hourly', 'Daily'])
const _value = ref('Hourly')
let animated = true

// TODO è buggato il componente, workaround
watch(
  value,
  (newValue, oldValue) => {
    if (newValue) {
      animated = true
      _value.value = newValue
    } else {
      setTimeout(() => (value.value = oldValue), 0)
    }
  }
)
const apiConsumptionToday = computed(() => {
  let total = 0
  Object.keys(params.value).forEach(day => {
    Object.keys(params.value[day]).forEach(hour => {
      if (new Date(Number(day) + (3600 * 1000 * Number(hour) + 86400000)) > new Date()) {
        total += Number(params.value[day][hour])
      }
    })
  })
  return total
})

const apiConsumptionDelta = computed(() => {
  let yestarday = 0
  Object.keys(params.value).forEach(day => {
    Object.keys(params.value[day]).forEach(hour => {
      if (new Date(Number(day) + (3600 * 1000 * Number(hour) + (2 * 86400000))) > new Date() && new Date(Number(day) + (3600 * 1000 * Number(hour) + 86400000)) < new Date()) {
        yestarday += Number(params.value[day][hour])
      }
    })
  })
  return Math.floor((apiConsumptionToday.value - yestarday) / yestarday * 100)
})

const chartDataHourly = computed(() => {
  const labels = Array.from({ length: 24 }, (_, i) => i)
  const tsOrdered = Object.keys(params.value).sort((a, b) => a - b)
  return {
    labels: labels.map(el => formatHours(el)),
    datasets: tsOrdered.map(day => {
      return {
        label: new Date(Number(day)).toLocaleDateString('en-IT', { weekday: 'short', month: 'short', day: 'numeric' }),
        tension: 0.1,
        data: labels.map(hour => params.value[day][hour] ? params.value[day][hour] : new Date(Number(day) + (3600 * 1000 * hour)) < new Date() ? 0 : undefined)
      }
    })
  }
})

const chartDataDaily = computed(() => {
  const tsOrdered = Object.keys(params.value).sort((a, b) => a - b)
  return {
    labels: tsOrdered.map(day => new Date(Number(day)).toLocaleDateString('en-IT', { weekday: 'short', month: 'short', day: 'numeric' })),
    datasets: [
      {
        backgroundColor: 'rgba(249, 115, 22, 0.8)',
        borderColor: 'rgb(249, 115, 22)',
        borderWidth: 1,
        data: tsOrdered.map(day => Object.values(params.value[day]).reduce((sum, x) => sum + Number(x), 0)),
      }
    ]
  }
})

const chartOptionsHourly = computed(() => {
  return {
    stacked: false,
    maintainAspectRatio: false,
    aspectRatio: 0.6,
    responsive: true,
    animation: () => {
      const r = animated
      animated = false
      return r
    },
    plugins: {
      legend: {
        labels: darkMode.value
          ? {
              color: 'rgb(204, 204, 204)'
            }
          : undefined,
        position: 'top'
      },
      tooltip: {
        enabled: true,
        xAlign: 'left',
        yAlign: 'center',
      },
    },
    scales: darkMode.value
      ? {
          x: {
            ticks: {
              color: '#888888'
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.2)'
            },
          },
          y: {
            ticks: {
              color: '#888888'
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.2)'
            }
          }
        }
      : undefined
  }
})
const chartOptionsDaily = computed(() => {
  return {
    stacked: false,
    maintainAspectRatio: false,
    aspectRatio: 0.6,
    responsive: true,
    animation: () => {
      const r = animated
      animated = false
      return r
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        xAlign: 'left',
        yAlign: 'center',
      },
    },
    scales: darkMode.value
      ? {
          x: {
            ticks: {
              color: '#888888'
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.2)'
            },
          },
          y: {
            ticks: {
              color: '#888888'
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.2)'
            }
          }
        }
      : undefined
  }
})

function formatHours (h) {
  const data = new Date()
  data.setHours(h)
  data.setMinutes(0)
  data.setSeconds(0)
  return data.toLocaleTimeString('en-IT', { hour: 'numeric', minute: 'numeric', hour12: true })
}

</script>
